// ══════════════════════════════════════════════
//  子组件：TransferDetailDrawer — 转移明细抽屉
// ══════════════════════════════════════════════
import React, { useState } from 'react';
import {
  Drawer, Card, Row, Col, Tag, Typography, Table, Input, Button, Space, message
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

const STATUS_TAG_MAP = {
  out: {
    pending:         { text: '待对方确认', color: 'orange' },
    completed:       { text: '对方已接收', color: 'green' },
    rejected:        { text: '对方已拒绝', color: 'red' },
    revoked:         { text: '我已撤销',   color: 'default' },
  },
  in: {
    pending_receive: { text: '待我接收',   color: 'orange' },
    received:        { text: '我已接收',   color: 'green' },
    rejected:        { text: '我已拒绝',   color: 'red' },
    revoked_by_other:{ text: '对方已撤销', color: 'default' },
  },
};

const TransferDetailDrawer = ({ open, record, onClose }) => {
  const [licenseSearch, setLicenseSearch] = useState('');

  if (!record) return null;

  const statusMap = STATUS_TAG_MAP[record.type] || {};
  const statusCfg = statusMap[record.status] || { text: record.status, color: 'default' };
  const isOut = record.type === 'out';

  const enterpriseLabel = isOut ? '接收企业' : '来源企业';
  const enterpriseName = isOut ? record.targetEnterprise : record.sourceEnterprise;
  const enterpriseId = isOut ? record.targetEnterpriseId : record.sourceEnterpriseId;

  const productLabel = isOut ? '转移产品' : '关联产品';
  const productName = record.productName;
  const productId = record.productId;

  const filteredLicenses = (record.licenses || []).filter(item =>
    item.license.toLowerCase().includes(licenseSearch.toLowerCase())
  );

  const licenseColumns = [
    {
      title: 'License',
      dataIndex: 'license',
      key: 'license',
      render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    {
      title: '设备状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => {
        if (s === 'transferring') return <Tag color="orange">转移中</Tag>;
        return <Tag color="green">正常</Tag>;
      },
    },
    {
      title: '绑定状态',
      dataIndex: 'bindStatus',
      key: 'bindStatus',
      width: 100,
      render: (s) => (
        <Tag color={s === '已绑定' ? 'success' : 'default'}>{s}</Tag>
      ),
    },
  ];

  return (
    <Drawer
      title="转移明细"
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {/* 转移信息卡片 */}
      <Card
        size="small"
        bordered={false}
        style={{ background: '#fafafa', borderRadius: 8, marginBottom: 16 }}
      >
        <Row gutter={[16, 12]}>
          <Col span={12}>
            <Text type="secondary">{enterpriseLabel}</Text>
            <div style={{ marginTop: 4 }}>
              <div style={{ fontWeight: 500 }}>{enterpriseName}</div>
              <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{enterpriseId}</Text>
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary">{productLabel}</Text>
            <div style={{ marginTop: 4 }}>
              <div style={{ fontWeight: 500 }}>{productName}</div>
              <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{productId}</Text>
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary">任务单号</Text>
            <div style={{ marginTop: 4, fontFamily: 'monospace', fontWeight: 500 }}>{record.id}</div>
          </Col>
          <Col span={12}>
            <Text type="secondary">发起时间</Text>
            <div style={{ marginTop: 4 }}>{record.createTime}</div>
          </Col>
          <Col span={12}>
            <Text type="secondary">转移状态</Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={statusCfg.color} style={{ border: 'none' }}>{statusCfg.text}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary">设备数量</Text>
            <div style={{ marginTop: 4, fontWeight: 500 }}>{record.deviceCount} 台</div>
          </Col>
          {record.remark && (
            <Col span={24}>
              <Text type="secondary">转移备注</Text>
              <div style={{ marginTop: 4 }}>{record.remark}</div>
            </Col>
          )}
          {record.rejectReason && (
            <Col span={24}>
              <Text type="secondary" style={{ color: '#ff4d4f' }}>拒绝原因</Text>
              <div style={{ marginTop: 4, color: '#ff4d4f' }}>{record.rejectReason}</div>
            </Col>
          )}
        </Row>
      </Card>

      {/* 设备列表 */}
      <div style={{ marginBottom: 12 }}>
        <Text strong>设备列表（{record.deviceCount}）</Text>
      </div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Input
            placeholder="搜索 License"
            style={{ width: 240 }}
            value={licenseSearch}
            onChange={(e) => setLicenseSearch(e.target.value)}
            onPressEnter={() => {}}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
          />
        </Space>
        <Button
          icon={<DownloadOutlined />}
          onClick={() => message.success('正在导出设备清单...')}
        >
          导出
        </Button>
      </div>
      <Table
        size="small"
        columns={licenseColumns}
        dataSource={filteredLicenses}
        rowKey="key"
        pagination={{ size: 'small', pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
        locale={{ emptyText: '未找到匹配的 License' }}
      />
    </Drawer>
  );
};

export default TransferDetailDrawer;
